import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Plus, Target, Trash2, Edit2, Check, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", className: "status-pending" },
  { value: "in_progress", label: "In Progress", className: "status-in_progress" },
  { value: "completed", label: "Completed", className: "status-completed" },
];

const MonthlyRocks = () => {
  const { token } = useAuth();
  const [salespeople, setSalespeople] = useState([]);
  const [rocks, setRocks] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingRock, setEditingRock] = useState(null);
  const [newRock, setNewRock] = useState({
    salesperson_id: "",
    description: "",
    status: "pending",
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [peopleRes, rocksRes] = await Promise.all([
        axios.get(`${API}/salespeople`, { headers }),
        axios.get(`${API}/rocks?year=${selectedYear}&month=${selectedMonth}`, { headers }),
      ]);
      setSalespeople(peopleRes.data);
      setRocks(rocksRes.data);
      if (peopleRes.data.length > 0 && !newRock.salesperson_id) {
        setNewRock((prev) => ({ ...prev, salesperson_id: peopleRes.data[0].id }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRock = async () => {
    if (!newRock.description.trim() || !newRock.salesperson_id) return;

    try {
      await axios.post(`${API}/rocks`, {
        ...newRock,
        year: selectedYear,
        month: selectedMonth,
      }, { headers });
      setNewRock({ salesperson_id: salespeople[0]?.id || "", description: "", status: "pending" });
      setIsAddDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error adding rock:", error);
    }
  };

  const handleUpdateStatus = async (rockId, newStatus) => {
    try {
      await axios.put(`${API}/rocks/${rockId}`, { status: newStatus }, { headers });
      fetchData();
    } catch (error) {
      console.error("Error updating rock:", error);
    }
  };

  const handleUpdateRock = async () => {
    if (!editingRock) return;

    try {
      await axios.put(`${API}/rocks/${editingRock.id}`, {
        description: editingRock.description,
        status: editingRock.status,
      }, { headers });
      setEditingRock(null);
      fetchData();
    } catch (error) {
      console.error("Error updating rock:", error);
    }
  };

  const handleDeleteRock = async (rockId) => {
    if (!window.confirm("Are you sure you want to delete this rock?")) return;

    try {
      await axios.delete(`${API}/rocks/${rockId}`, { headers });
      fetchData();
    } catch (error) {
      console.error("Error deleting rock:", error);
    }
  };

  const getPersonName = (personId) => {
    const person = salespeople.find((sp) => sp.id === personId);
    return person?.name || "Unknown";
  };

  // Group rocks by salesperson
  const rocksByPerson = salespeople.map((person) => ({
    person,
    rocks: rocks.filter((r) => r.salesperson_id === person.id),
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="monthly-rocks-page">
      {/* Header */}
      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Monthly Rocks</h1>
          <p className="page-subtitle">
            Team goals and priorities for {MONTHS[selectedMonth - 1]} {selectedYear}
          </p>
        </div>

        <div className="flex gap-3">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="form-input w-auto"
            data-testid="month-select"
          >
            {MONTHS.map((month, index) => (
              <option key={month} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="form-input w-auto"
            data-testid="year-select"
          >
            {[2024, 2025, 2026, 2027].map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => setIsAddDialogOpen(true)}
            data-testid="add-rock-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Rock
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-6">
        <p className="text-sm text-indigo-800">
          <strong>A Rock</strong> is a single, clearly defined priority that must be completed within a 30-day period 
          to move the business forward. It is not a task list or a wish. It is a measurable outcome with an owner and 
          a deadline. Rocks create focus, eliminate noise, and force leadership to commit to what truly matters right now.
        </p>
      </div>

      {/* Rocks by Person */}
      <div className="space-y-6">
        {rocksByPerson.map(({ person, rocks: personRocks }) => (
          <div key={person.id} className="chart-container" data-testid={`person-rocks-${person.id}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold">
                {person.name.charAt(0)}
              </div>
              <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {person.name}
              </h3>
              <span className="text-sm text-slate-500">
                {personRocks.length} rock{personRocks.length !== 1 ? "s" : ""}
              </span>
            </div>

            {personRocks.length > 0 ? (
              <div className="space-y-3">
                {personRocks.map((rock, index) => (
                  <div
                    key={rock.id}
                    className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg group"
                    data-testid={`rock-${index}`}
                  >
                    {editingRock?.id === rock.id ? (
                      // Edit mode
                      <div className="flex-1 space-y-3">
                        <Textarea
                          value={editingRock.description}
                          onChange={(e) =>
                            setEditingRock({ ...editingRock, description: e.target.value })
                          }
                          className="min-h-[80px]"
                        />
                        <div className="flex items-center gap-2">
                          <Select
                            value={editingRock.status}
                            onValueChange={(v) => setEditingRock({ ...editingRock, status: v })}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleUpdateRock}
                            className="text-emerald-600 hover:text-emerald-700"
                          >
                            <Check size={18} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingRock(null)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            <X size={18} />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <>
                        <div className="w-6 h-6 mt-0.5 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <Target size={14} className="text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-slate-800">{rock.description}</p>
                          <div className="mt-2 flex items-center gap-3">
                            <Select
                              value={rock.status}
                              onValueChange={(v) => handleUpdateStatus(rock.id, v)}
                            >
                              <SelectTrigger className="w-[140px] h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingRock(rock)}
                            className="text-slate-400 hover:text-slate-600"
                            data-testid={`edit-rock-${index}`}
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteRock(rock.id)}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`delete-rock-${index}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm italic">No rocks defined for this month</p>
            )}
          </div>
        ))}
      </div>

      {/* Add Rock Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent data-testid="add-rock-dialog">
          <DialogHeader>
            <DialogTitle>Add New Rock</DialogTitle>
            <DialogDescription>
              Define a monthly priority for a team member.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Salesperson</Label>
              <Select
                value={newRock.salesperson_id}
                onValueChange={(v) => setNewRock({ ...newRock, salesperson_id: v })}
              >
                <SelectTrigger data-testid="rock-salesperson-select">
                  <SelectValue placeholder="Select person" />
                </SelectTrigger>
                <SelectContent>
                  {salespeople.map((person) => (
                    <SelectItem key={person.id} value={person.id}>
                      {person.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the rock/priority..."
                value={newRock.description}
                onChange={(e) => setNewRock({ ...newRock, description: e.target.value })}
                className="min-h-[100px]"
                data-testid="rock-description-input"
              />
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={newRock.status}
                onValueChange={(v) => setNewRock({ ...newRock, status: v })}
              >
                <SelectTrigger data-testid="rock-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddRock}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-rock-btn"
            >
              Add Rock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthlyRocks;
